import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  Users,
  CreditCard,
  HelpCircle,
  Search,
  ChevronDown,
  KeyRound,
  LogOut,
  Blocks,
  BoxIcon,
  WalletCards,
  Menu,
  X,
} from 'lucide-react';

const Sidebar = React.memo(({ mobileOpen, onMobileClose }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [user, setUser] = useState({
    username: 'Loading...',
    email: 'Loading...',
  });
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (!storedUser?.email) {
      navigate('/login');
      return;
    }
    setUser(storedUser);
  }, [navigate]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (mobileOpen) onMobileClose?.();
  }, [navigate]);

  // Handle Ctrl+K shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close dropdown when sidebar closes on mobile
  useEffect(() => {
    if (!mobileOpen) setDropdownOpen(false);
  }, [mobileOpen]);

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/bookings', label: 'Bookings', icon: Calendar },
    { path: '/resources', label: 'Resources', icon: Blocks },
    { path: '/products', label: 'Products', icon: BoxIcon },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/plans', label: 'Plans', icon: CreditCard },
    { path: '/finance', label: 'Finance', icon: WalletCards },
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('access_token');
    setDropdownOpen(false);
    onMobileClose?.();
    setTimeout(() => navigate('/login', { replace: true }), 0);
  };

  const handleResetPassword = async () => {
    try {
      const response = await fetch(
        `https://farpoint.us.auth0.com/dbconnections/change_password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_id: 'kDrr9cGuOqVO28GYl8x05hroSYgpGTdH',
            email: user.email,
            connection: 'Username-Password-Authentication',
          }),
        }
      );

      if (response.ok) {
        setIsOverlayOpen(true);
        setDropdownOpen(false);
        setTimeout(() => setIsOverlayOpen(false), 3000);
      } else {
        const errorData = await response.json();
        console.error('Password reset error:', errorData);
      }
    } catch (error) {
      console.error('Error sending password reset email:', error);
    }
  };

  const sidebarContent = (
    <aside
      className="flex h-full bg-white flex-col"
      style={{ width: '232px' }}
    >
      {/* Logo Section */}
      <div className="px-4 py-5 flex items-center justify-center">
        <div className="flex items-center gap-1">
          <img
            src="/logo.png"
            alt="Optimus Logo"
            className="w-48 h-auto object-contain"
          />
        </div>
        {/* Close button — mobile only */}
        <button
          className="md:hidden p-1 ml-3 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
          onClick={onMobileClose}
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Search Bar */}
      <div className="px-4 pb-4 mb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search"
            className="w-full bg-gray-100 rounded-md border-0 px-3 py-2 pl-9 text-sm text-gray-700 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:outline-none focus:bg-white"
            style={{ fontFamily: 'Inter' }}
          />
          <span className="absolute right-[36px] top-1/2 transform -translate-y-1/2 bg-blue-100 text-blue-700 text-xs font-semibold px-1.5 py-1 rounded">
            ⌘
          </span>
          <span className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-100 text-blue-700 font-medium text-xs px-1.5 py-1 rounded">
            K
          </span>
        </div>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 px-2 overflow-y-auto">
        <div className="space-y-2.5">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={false}
                onClick={onMobileClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-2.5 px-3 rounded-md text-sm transition-all duration-150
                  ${
                    isActive
                      ? 'bg-gray-100 text-gray-900 font-medium shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
                style={{ fontFamily: 'Inter' }}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className="w-5 h-5"
                      strokeWidth={isActive ? 2 : 1.5}
                    />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* User Profile Section */}
      <div className="border-t border-gray-200 p-3">
        <div
          className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors relative"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <div className="w-9 h-9 rounded-full bg-[#1A5090] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-sm text-gray-900 font-medium truncate"
              style={{ fontFamily: 'Inter' }}
            >
              {user.username}
            </p>
            <p
              className="text-xs text-gray-500 truncate"
              style={{ fontFamily: 'Inter' }}
            >
              {user.email}
            </p>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
              dropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </div>

        {dropdownOpen && (
          <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
              style={{ fontFamily: 'Inter' }}
              onClick={(e) => {
                e.stopPropagation();
                handleResetPassword();
              }}
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Reset Password
            </button>
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100 flex items-center"
              style={{ fontFamily: 'Inter' }}
              onClick={(e) => {
                e.stopPropagation();
                handleLogout();
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <>
      {/* ── Desktop: always-visible sticky sidebar ── */}
      <div className="hidden md:flex sticky left-0 top-0 h-screen">
        {sidebarContent}
      </div>

      {/* ── Mobile: slide-over drawer ── */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onMobileClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={`fixed top-0 left-0 z-50 h-full shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </div>

      {/* Password Reset Overlay */}
      {isOverlayOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[60]">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center max-w-sm mx-4">
            <h2
              className="text-lg font-semibold text-gray-900 mb-2"
              style={{ fontFamily: 'Inter' }}
            >
              Password Reset Sent
            </h2>
            <p className="text-gray-600 text-sm" style={{ fontFamily: 'Inter' }}>
              A reset link has been sent to{' '}
              <strong>{user.email}</strong>. Please check your inbox.
            </p>
          </div>
        </div>
      )}
    </>
  );
});

Sidebar.displayName = 'Sidebar';
export default Sidebar;